import { Users, Briefcase } from "lucide-react";
import { useContent, resolveImage } from "../contexts/ContentContext";

const getInitials = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const ACCENTS = [
  { bg: "bg-[#2563EB]", text: "text-white" },
  { bg: "bg-[#FFD100]", text: "text-[#0F172A]" },
  { bg: "bg-[#1E3A8A]", text: "text-white" },
  { bg: "bg-[#1D4ED8]", text: "text-white" },
];

export const BoardMembers = () => {
  const { data: board } = useContent("board");
  if (!board) return null;

  const members = board.members || [];

  return (
    <section id="board" data-testid="board-section" className="py-20 md:py-28 bg-white relative overflow-hidden">
      <div aria-hidden="true" className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-[#FFD100]/15 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 left-0 w-96 h-96 rounded-full bg-[#2563EB]/12 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="section-eyebrow">
              <Users className="w-3.5 h-3.5" />
              {board.eyebrow}
            </span>
            <h2 data-testid="board-heading" className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              {board.heading_lead}
              <span className="text-gradient-brand"> {board.heading_accent}</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-slate-600 max-w-lg leading-relaxed">{board.intro}</p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-testid="board-grid">
          {members.map((m, i) => {
            const accent = ACCENTS[i % ACCENTS.length];
            const photo = resolveImage(m.photo);
            return (
              <article key={i} data-testid={`board-member-${i}`} className="card-soft p-6 md:p-7 flex flex-col group">
                {photo ? (
                  <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white shadow-[0_10px_30px_rgba(37,99,235,0.18)]">
                    <img src={photo} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                ) : (
                  <div className={`w-20 h-20 rounded-full ${accent.bg} ${accent.text} grid place-items-center font-display text-xl font-extrabold ring-4 ring-white shadow-[0_10px_30px_rgba(37,99,235,0.18)]`} aria-hidden="true">
                    {getInitials(m.name)}
                  </div>
                )}
                <p className="mt-5 font-display text-lg md:text-xl font-bold text-slate-900 leading-tight">{m.name}</p>
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-[#1D4ED8]">
                  <Briefcase className="w-3 h-3" strokeWidth={2.5} />
                  {m.short}
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{m.role}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
